import type { UIMessage } from "ai";
import { ArrowDown, Pause, Send } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { cn } from "@/lib/utils";
import type { BrandAssistantSelectedAssetReference } from "@runcheetah/shared";
import { Button } from "@runcheetah/ui";
import { Textarea } from "@runcheetah/ui";

import {
  AssistantChatMessage,
  AssistantRuntimeActivity,
  AssistantRuntimeStatus,
} from "./assistant-chat-message";
import {
  ASSISTANT_RUNTIME_THOUGHT_TICK_MS,
  getAssistantMessageIdForRuntime,
  getAssistantLatestScrollTop,
  getAssistantRealContentEnd,
  getAssistantRunwayHeight,
  getAssistantRuntimeThought,
  getAssistantThreadScrollKey,
  getLatestMessageScrollKey,
  getLatestUserMessageRuntimeKey,
  getAssistantThreadScrollAction,
  getAssistantUserAnchorScrollTop,
  isAtLatestMessageEdge,
  shouldShowAssistantRuntimeActivityForMessage,
  shouldShowAssistantRuntimeStatus,
  type AssistantChatStatus,
  type AssistantRuntimeStepList,
} from "./assistant-chat-helpers";

type AssistantRuntimeStatusState = {
  key: string;
  startedAtMs: number;
};

const ASSISTANT_USER_ANCHOR_OFFSET_PX = 64;

export function AssistantChatPanel({
  messages,
  status,
  draft,
  error,
  topLabel,
  topActions,
  threadMeta,
  threadToolbarActions,
  showThreadToolbar = true,
  emptyState,
  extraContent,
  transientTimelineActivity,
  composerContext,
  placeholder,
  className,
  contentClassName,
  messageListClassName,
  composerContainerClassName,
  inputClassName,
  isOpen = true,
  renderAssistantMessageExtra,
  assistantMessageExtraPlacement,
  assistantMessageContentClassName,
  assistantMessageExtraClassName,
  getAssistantMessageContent,
  shouldRenderAssistantMessageContent,
  renderSelectedAssetReference,
  disableRuntimeThoughts = false,
  runtimeActivity,
  showActivityParts = true,
  runtimeThoughts,
  onDraftChange,
  onSubmit,
  onClear,
  onStop,
  onClearError,
  onRetry,
  isRetrying = false,
}: {
  messages: UIMessage[];
  status: AssistantChatStatus;
  draft: string;
  error?: Error | null;
  topLabel?: ReactNode;
  topActions?: ReactNode;
  threadMeta?: ReactNode;
  threadToolbarActions?: ReactNode;
  showThreadToolbar?: boolean;
  emptyState: ReactNode;
  extraContent?: ReactNode;
  transientTimelineActivity?: ReactNode;
  composerContext?: ReactNode;
  placeholder: string;
  className?: string;
  contentClassName?: string;
  messageListClassName?: string;
  composerContainerClassName?: string;
  inputClassName?: string;
  isOpen?: boolean;
  renderAssistantMessageExtra?: (message: UIMessage) => ReactNode;
  assistantMessageExtraPlacement?: "beforeContent" | "afterContent";
  assistantMessageContentClassName?: string;
  assistantMessageExtraClassName?: string;
  getAssistantMessageContent?: (message: UIMessage) => string;
  shouldRenderAssistantMessageContent?: (message: UIMessage) => boolean;
  renderSelectedAssetReference?: (reference: BrandAssistantSelectedAssetReference) => ReactNode;
  disableRuntimeThoughts?: boolean;
  runtimeActivity?: ReactNode;
  showActivityParts?: boolean;
  runtimeThoughts?: AssistantRuntimeStepList;
  onDraftChange: (value: string) => void;
  onSubmit: (value: string) => Promise<void> | void;
  onClear?: () => void;
  onStop: () => void;
  onClearError?: () => void;
  onRetry?: () => Promise<void> | void;
  isRetrying?: boolean;
}) {
  const intl = useIntl();
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const realContentRef = useRef<HTMLDivElement | null>(null);
  const runwayRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messageElementsRef = useRef(new Map<string, HTMLDivElement>());
  const anchoredUserMessageIdRef = useRef<string | null>(null);
  const currentThreadLineageRef = useRef<string | null>(null);
  const isBusy = shouldShowAssistantRuntimeStatus(status);
  const latestRuntimeMessageKey = getLatestUserMessageRuntimeKey(messages);
  const [runtimeStatus, setRuntimeStatus] = useState<AssistantRuntimeStatusState | null>(null);
  const [runtimeNowMs, setRuntimeNowMs] = useState(() => Date.now());
  const runtimeAssistantMessageId = runtimeStatus
    ? getAssistantMessageIdForRuntime(messages, runtimeStatus.key)
    : null;
  const runtimeThought =
    runtimeStatus && !runtimeActivity && !disableRuntimeThoughts
      ? getAssistantRuntimeThought(runtimeNowMs - runtimeStatus.startedAtMs, runtimeThoughts)
      : null;
  const latestMessageScrollKey = getLatestMessageScrollKey(messages);
  const threadLineageKey = getAssistantThreadScrollKey(messages);

  const getRealContentEnd = useCallback(() => {
    const scrollRoot = scrollRef.current;
    const realContent = realContentRef.current;
    if (!scrollRoot || !realContent) {
      return null;
    }

    const scrollRootRect = scrollRoot.getBoundingClientRect();
    const contentRect = realContent.getBoundingClientRect();
    const bottomInset = Number.parseFloat(window.getComputedStyle(scrollRoot).paddingBottom) || 0;
    return getAssistantRealContentEnd({
      currentScrollTop: scrollRoot.scrollTop,
      scrollRootTop: scrollRootRect.top,
      contentBottom: contentRect.bottom,
      bottomInset,
    });
  }, []);

  const updateRunway = useCallback(
    (userMessageId: string | null = anchoredUserMessageIdRef.current) => {
      const scrollRoot = scrollRef.current;
      const runway = runwayRef.current;
      const messageElement = userMessageId ? messageElementsRef.current.get(userMessageId) : null;
      if (!scrollRoot || !runway) {
        return;
      }

      const realContentEnd = getRealContentEnd();
      if (!messageElement || realContentEnd === null) {
        runway.style.height = "0px";
        return;
      }

      const scrollRootRect = scrollRoot.getBoundingClientRect();
      const messageRect = messageElement.getBoundingClientRect();
      const topInset =
        (Number.parseFloat(window.getComputedStyle(scrollRoot).paddingTop) || 0) +
        ASSISTANT_USER_ANCHOR_OFFSET_PX;
      const anchorScrollTop = getAssistantUserAnchorScrollTop({
        currentScrollTop: scrollRoot.scrollTop,
        scrollRootTop: scrollRootRect.top,
        messageTop: messageRect.top,
        topInset,
      });
      runway.style.height = `${getAssistantRunwayHeight({
        anchorScrollTop,
        clientHeight: scrollRoot.clientHeight,
        realContentEnd,
      })}px`;
    },
    [getRealContentEnd],
  );

  const scrollMessagesToLatest = useCallback(
    (behavior: ScrollBehavior) => {
      const scrollRoot = scrollRef.current;
      if (!scrollRoot) {
        return;
      }

      window.requestAnimationFrame(() => {
        updateRunway();
        const realContentEnd = getRealContentEnd();
        if (realContentEnd === null) {
          return;
        }

        scrollRoot.scrollTo({
          top: getAssistantLatestScrollTop({
            clientHeight: scrollRoot.clientHeight,
            realContentEnd,
          }),
          behavior,
        });
        setShowJumpToLatest(false);
      });
    },
    [getRealContentEnd, updateRunway],
  );

  const updateJumpState = useCallback(() => {
    const scrollRoot = scrollRef.current;
    const realContentEnd = getRealContentEnd();
    if (!scrollRoot || realContentEnd === null) {
      return;
    }

    setShowJumpToLatest(!isAtLatestMessageEdge(scrollRoot, realContentEnd));
  }, [getRealContentEnd]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const scrollRoot = scrollRef.current;
    const realContent = realContentRef.current;
    if (!scrollRoot || !realContent) {
      return;
    }

    let frame = 0;
    const updateMeasurements = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        updateRunway();
        updateJumpState();
      });
    };

    updateRunway();
    updateJumpState();
    const observer = new ResizeObserver(updateMeasurements);
    observer.observe(scrollRoot);
    observer.observe(realContent);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [isOpen, updateJumpState, updateRunway]);

  useLayoutEffect(() => {
    if (!isOpen || messages.length === 0) {
      return;
    }

    const scrollRoot = scrollRef.current;
    if (!scrollRoot) {
      return;
    }

    const threadLineageChanged = currentThreadLineageRef.current !== threadLineageKey;
    const action = getAssistantThreadScrollAction({
      isBusy,
      latestUserMessageId: latestRuntimeMessageKey,
      anchoredUserMessageId: anchoredUserMessageIdRef.current,
      threadLineageChanged,
    });
    currentThreadLineageRef.current = threadLineageKey;

    if (action.type === "scroll-to-latest") {
      anchoredUserMessageIdRef.current = null;
      updateRunway(null);
      const realContentEnd = getRealContentEnd();
      if (realContentEnd === null) {
        return;
      }

      scrollRoot.scrollTo({
        top: getAssistantLatestScrollTop({
          clientHeight: scrollRoot.clientHeight,
          realContentEnd,
        }),
        behavior: "auto",
      });
      setShowJumpToLatest(false);
      return;
    }

    if (action.type !== "anchor-user") {
      return;
    }

    const messageElement = messageElementsRef.current.get(action.messageId);
    if (!messageElement) {
      return;
    }

    anchoredUserMessageIdRef.current = action.messageId;
    updateRunway(action.messageId);
    const scrollRootRect = scrollRoot.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();
    const topInset =
      (Number.parseFloat(window.getComputedStyle(scrollRoot).paddingTop) || 0) +
      ASSISTANT_USER_ANCHOR_OFFSET_PX;
    scrollRoot.scrollTo({
      top: getAssistantUserAnchorScrollTop({
        currentScrollTop: scrollRoot.scrollTop,
        scrollRootTop: scrollRootRect.top,
        messageTop: messageRect.top,
        topInset,
      }),
      behavior: "auto",
    });
    updateJumpState();
  }, [
    isBusy,
    getRealContentEnd,
    isOpen,
    latestRuntimeMessageKey,
    messages.length,
    threadLineageKey,
    updateJumpState,
    updateRunway,
  ]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updateJumpState();
  }, [
    extraContent,
    isOpen,
    latestMessageScrollKey,
    runtimeActivity,
    transientTimelineActivity,
    updateJumpState,
  ]);

  useEffect(() => {
    if (isBusy && latestRuntimeMessageKey) {
      setRuntimeStatus((currentStatus) =>
        currentStatus?.key === latestRuntimeMessageKey
          ? currentStatus
          : { key: latestRuntimeMessageKey, startedAtMs: Date.now() },
      );
      setRuntimeNowMs(Date.now());
      return;
    }

    setRuntimeStatus(null);
  }, [isBusy, latestRuntimeMessageKey]);

  useEffect(() => {
    if (!runtimeStatus) {
      return;
    }

    const interval = window.setInterval(
      () => setRuntimeNowMs(Date.now()),
      ASSISTANT_RUNTIME_THOUGHT_TICK_MS,
    );

    return () => window.clearInterval(interval);
  }, [runtimeStatus]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  }, [draft]);

  const getMessageRuntimeActivity = (message: UIMessage) => {
    if (message.role !== "assistant") {
      return null;
    }

    if (runtimeAssistantMessageId !== message.id) {
      return null;
    }

    if (!shouldShowAssistantRuntimeActivityForMessage(message)) {
      return null;
    }

    if (runtimeActivity) {
      return runtimeActivity;
    }

    if (runtimeThought) {
      return <AssistantRuntimeActivity isActive={isBusy} thought={runtimeThought} />;
    }

    return null;
  };

  const submitDraft = async () => {
    const value = draft.trim();
    if (!value || isBusy) {
      return;
    }

    onClearError?.();
    onDraftChange("");
    await onSubmit(value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitDraft();
  };

  return (
    <aside
      className={cn(
        "relative flex h-full min-h-0 flex-col text-card-foreground shadow-2xl shadow-foreground/8 backdrop-blur xl:shadow-none",
        className,
      )}
    >
      {topLabel || topActions ? (
        <div className="flex items-center justify-between gap-4 border-b border-border/70 px-5 py-4">
          {topLabel ? (
            <div className="text-xs uppercase tracking-[0.16em] text-foreground/48">{topLabel}</div>
          ) : null}
          {topActions ? <div className="flex items-center gap-1.5">{topActions}</div> : null}
        </div>
      ) : null}

      {showThreadToolbar ? (
        <div
          className={cn(
            "flex items-center gap-2 border-b border-border/60 px-5 py-3",
            threadMeta ? "justify-between" : "justify-end",
          )}
        >
          {threadMeta ? (
            <div className="flex items-center gap-2 text-xs text-foreground/58">{threadMeta}</div>
          ) : null}
          <div className="flex items-center gap-1.5">
            {threadToolbarActions}
            {onClear ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 rounded-full px-2.5 text-xs text-foreground/65"
                onClick={onClear}
                disabled={messages.length === 0 && draft.length === 0}
              >
                <FormattedMessage id="common.clear" defaultMessage="Clear" />
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        onScroll={updateJumpState}
        className={cn(
          "min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-5 [overflow-anchor:none]",
          contentClassName,
        )}
      >
        <div ref={realContentRef} className={cn("shrink-0", messages.length === 0 && "h-full")}>
          {messages.length === 0 ? (
            emptyState
          ) : (
            <div className={cn("min-w-0 max-w-full space-y-4", messageListClassName)}>
              {messages.map((message) => {
                const messageRuntimeActivity = getMessageRuntimeActivity(message);

                return (
                  <div
                    key={message.id}
                    ref={(element) => {
                      if (element) {
                        messageElementsRef.current.set(message.id, element);
                      } else {
                        messageElementsRef.current.delete(message.id);
                      }
                    }}
                  >
                    <AssistantChatMessage
                      message={message}
                      activityHistory={messageRuntimeActivity}
                      extra={renderAssistantMessageExtra?.(message)}
                      assistantExtraPlacement={assistantMessageExtraPlacement}
                      assistantContentClassName={assistantMessageContentClassName}
                      assistantExtraClassName={assistantMessageExtraClassName}
                      getMessageContent={getAssistantMessageContent}
                      showContent={shouldRenderAssistantMessageContent?.(message) ?? true}
                      renderSelectedAssetReference={renderSelectedAssetReference}
                      showActivityParts={showActivityParts}
                    />
                  </div>
                );
              })}
              {transientTimelineActivity}
              {extraContent}
            </div>
          )}

          {runtimeStatus && !runtimeAssistantMessageId ? (
            runtimeActivity ? (
              <div
                className="mt-4 pr-8 text-sm leading-6 sm:pr-16"
                role="status"
                aria-live="polite"
              >
                {runtimeActivity}
              </div>
            ) : (
              <AssistantRuntimeStatus
                key={runtimeStatus.key}
                isActive={isBusy}
                thought={runtimeThought}
              />
            )
          ) : null}
        </div>
        {messages.length > 0 ? (
          <div ref={runwayRef} className="shrink-0" aria-hidden="true" />
        ) : null}
      </div>

      {showJumpToLatest ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="absolute bottom-22 left-1/2 z-10 h-9 w-9 -translate-x-1/2 rounded-full border border-foreground/12 bg-card/95 text-foreground/80 shadow-[0_10px_28px_-14px_rgba(19,34,49,0.65)] backdrop-blur-md transition hover:border-foreground/20 hover:bg-card hover:text-foreground"
          onClick={() => scrollMessagesToLatest("smooth")}
          aria-label={intl.formatMessage({
            id: "assistantChat.jumpToLatest",
            defaultMessage: "Jump to latest",
          })}
        >
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      ) : null}

      <div className={cn("w-full", composerContainerClassName)}>
        {error || onRetry ? (
          <div
            className={cn(
              "mb-3 flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 text-sm",
              error
                ? "border-destructive/20 bg-destructive/8 text-destructive"
                : "border-border/70 bg-muted/45 text-foreground",
            )}
          >
            <span>
              {error?.message ||
                intl.formatMessage({
                  id: "assistantChat.responseIncomplete",
                  defaultMessage: "Cheetah didn't finish this response.",
                })}
            </span>
            {onRetry ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 shrink-0 rounded-full px-3 text-xs"
                disabled={isRetrying}
                onClick={() => void onRetry()}
              >
                <FormattedMessage id="common.retry" defaultMessage="Retry" />
              </Button>
            ) : null}
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className="flex items-end gap-1.5 rounded-[1.35rem] border border-border/70 bg-card/90 p-1.5 shadow-[0_18px_60px_-38px_rgba(19,34,49,0.65)] backdrop-blur-md transition focus-within:border-foreground/25 focus-within:ring-2 focus-within:ring-primary/12">
            <div className="min-w-0 flex-1">
              {composerContext ? <div className="px-2 pb-1.5">{composerContext}</div> : null}
              <Textarea
                ref={textareaRef}
                value={draft}
                onChange={(event) => {
                  if (error) {
                    onClearError?.();
                  }
                  onDraftChange(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void submitDraft();
                  }
                }}
                rows={1}
                placeholder={placeholder}
                className={cn(
                  "max-h-[180px] min-h-9 resize-none overflow-y-auto rounded-[1.1rem] border-0 bg-transparent px-3 py-2 text-sm leading-5 shadow-none focus-visible:border-transparent focus-visible:ring-0",
                  inputClassName,
                )}
              />
            </div>
            {isBusy ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full"
                onClick={onStop}
                aria-label={intl.formatMessage({ id: "common.stop", defaultMessage: "Stop" })}
              >
                <Pause className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full disabled:bg-transparent disabled:text-muted-foreground/45 disabled:opacity-60"
                disabled={!draft.trim()}
                aria-label={intl.formatMessage({ id: "common.send", defaultMessage: "Send" })}
              >
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
        </form>
      </div>
    </aside>
  );
}

export {
  appendAssistantLiveActivityUpdate,
  getAssistantActivityParts,
  getAssistantCompactionEvent,
  getAssistantMessageDisplayBlocks,
  getAssistantMessageIdForRuntime,
  getAssistantLatestScrollTop,
  getAssistantMessageText,
  getAssistantRealContentEnd,
  getAssistantRunwayHeight,
  getAssistantSelectedAssetReferences,
  getAssistantSelectedImageReferences,
  getAssistantTraceEntries,
  getAssistantRuntimeThought,
  getAssistantSourceDomain,
  getAssistantThreadScrollAction,
  getAssistantThreadScrollKey,
  getAssistantToolStatusLabel,
  getAssistantTips,
  getAssistantWebSearchQuery,
  getAssistantWebSearchSourceCount,
  getLatestAssistantMessageAfterLatestUser,
  getLatestAssistantTipsAfterLatestUser,
  getInternalAssistantHref,
  getLatestMessageScrollKey,
  getLatestUserMessageRuntimeKey,
  getAssistantUserAnchorScrollTop,
  getPersistedAssistantStreamActivityUpdates,
  getSafeAssistantImageSrc,
  getSafeAssistantSourceUrl,
  hasActiveAssistantMessagePart,
  hasActivePersistedAssistantStream,
  hasRenderableAssistantMessageContent,
  isAtLatestMessageEdge,
  isInternalAssistantHref,
  parseAssistantSourceReference,
  shouldRenderAssistantStructuredExtras,
  shouldShowAssistantRuntimeActivityForMessage,
  shouldShowAssistantRuntimeStatus,
  shouldShowJumpToLatest,
} from "./assistant-chat-helpers";

export type { AssistantChatStatus } from "./assistant-chat-helpers";
